import { defaultJobDraftV2, OrderStateV2 } from './context';

// Pure reducers for the two orchestrator setState callbacks that re-check
// race-prone guards inside the updater. Extracted from `index.tsx` so they
// can be unit-tested independently of the hook implementation, and so a
// future React Query swap of `useCertificates` / `useRateQuote` cannot
// silently regress the "don't clobber template prefill" or "don't clobber
// user-entered rates" behavior.

// Mirrors the backend's TemplateFingerprintService#normalize_text
// (`value.to_s.strip.gsub(/\s+/, " ").downcase`) so the frontend dedupe
// keys off the exact same canonical form the server used to fingerprint
// the template — two jobs the backend considered identical must hash
// identically here too.
const normalizeTemplateText = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

// A repeat-order template persists the *submission* payload, not the
// wizard's job drafts. `buildCreatePayload` fans a single logical job
// draft out into one entry per shift/day (per-shift expansion), and the
// backend stores those expanded entries verbatim. Re-applying that
// payload as-is surfaces a single-skill repeat as N blank duplicate
// skill cards (one per original shift), each with an empty schedule —
// the spurious "added skill" card on the Submission step.
//
// Collapse entries that share the same logical job identity back into
// one. The key set must match the backend
// `Orders::V2::TemplateFingerprintService#canonical_jobs` identity —
// otherwise the wizard could dedupe to N cards while the backend
// fingerprints to M and the upsert decides "same template" or "new
// template" against a different identity than the user sees. Both
// drop the per-shift-varying fields (`quantity`,
// `mandatory_break_time`, `shifts_count`) because the per-shift
// expansion stamps those onto every emitted clone, and they also drop
// `is_holiday` because it's derived per-shift from surge-date
// detection in `buildCreatePayload` — leaving it in would split a
// surge-spanning multi-shift job into two cards and would also have
// kept the backend fingerprint date-dependent (the bug QA reported
// where repeat orders re-entering schedules created duplicate
// templates). The remaining fields (skill/address/contact/uniform +
// normalized instruction/flag fields + certificates) keep a genuine
// multi-skill / multi-location template's distinct entries apart.
// First occurrence wins, so order is preserved.
const templateJobIdentity = (job: Record<string, unknown>): string => {
  const certificates = Array.isArray(job.certificates)
    ? [...(job.certificates as unknown[])]
        .map((c) => Number(c))
        .sort((a, b) => a - b)
    : [];
  return JSON.stringify({
    skill_id: job.skill_id ?? null,
    address_id: job.address_id ?? null,
    contact_id: job.contact_id ?? null,
    uniform_id: job.uniform_id ?? null,
    job_type: String(job.job_type ?? '').toUpperCase(),
    instructions: normalizeTemplateText(job.instructions),
    address_instructions: normalizeTemplateText(job.address_instructions),
    contact_instructions: normalizeTemplateText(job.contact_instructions),
    uniform_instructions: normalizeTemplateText(job.uniform_instructions),
    share_contact_number: !!job.share_contact_number,
    all_shifts_same_worker: !!job.all_shifts_same_worker,
    auto_accept_requests: !!job.auto_accept_requests,
    certificates,
  });
};

export const dedupeTemplateJobs = <T extends Record<string, unknown>>(
  jobs: T[],
): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const job of jobs) {
    const key = templateJobIdentity(job);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(job);
  }
  return out;
};

// Auto-seed the customer's default certificate ids onto `jobs[0].certificates`
// — but only when the wizard wasn't prefilled from a template AND no
// certificates have been picked yet. Mirrors the legacy behavior of the
// `useCertificates` callback inside the orchestrator.
export const autoSeedCertificateDefaultsReducer = (
  prev: OrderStateV2,
  defaultIds: number[],
): OrderStateV2 => {
  if (prev.isPrefilled) return prev;
  if (prev.jobs.length === 0) return prev;
  const job = prev.jobs[0];
  if ((job.certificates || []).length > 0) return prev;
  const nextJobs = [...prev.jobs];
  nextJobs[0] = { ...job, certificates: defaultIds };
  return { ...prev, jobs: nextJobs };
};

// Start a new skill from the Review step. Prepends a fresh empty draft
// to `jobs[0]` so the wizard (which always reads/writes `jobs[0]`) edits
// the new skill while previously completed jobs are preserved at indices
// 1+. `pendingNewJob` records that the prepended draft must later
// rotate to the end of the array so the on-screen / submission order
// matches V1's append semantics (`finishNewJobDraftReducer`). Without
// the rotate, the latest-added skill would display first on Review and
// also serialize first in the template, leaking reversed order into
// repeat orders.
export const addNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => ({
  ...prev,
  jobs: [defaultJobDraftV2(prev.orderType || 'GIG'), ...prev.jobs],
  pendingNewJob: true,
});

// Cancel an in-progress new skill draft and fall back to the previously
// completed job(s). No-op when `jobs.length <= 1` so a single-skill order
// can't accidentally drop its only job — the wizard's existing "Cancel"
// button still handles that case by leaving the wizard altogether.
export const cancelNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => {
  if (prev.jobs.length <= 1) return prev;
  return { ...prev, jobs: prev.jobs.slice(1), pendingNewJob: false };
};

// Finalize an add-skill flow on Continue-into-Review: rotate the
// freshly added `jobs[0]` to the end of the array (V1 parity — first-
// added first, last-added last) and clear the `pendingNewJob` flag.
// No-op when there's no pending new job, no second slot to rotate
// against, or when the user is editing a job in place (the edit flow
// keeps its own snapshot/restore semantics — the rotation here would
// reorder an edit's source instead of the new-skill source).
export const finishNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => {
  if (!prev.pendingNewJob) return prev;
  if (prev.jobs.length <= 1) return { ...prev, pendingNewJob: false };
  if (prev.editSnapshot) return { ...prev, pendingNewJob: false };
  const [head, ...rest] = prev.jobs;
  return { ...prev, jobs: [...rest, head], pendingNewJob: false };
};

// Begin an in-place edit of an existing job from the Review step. The
// whole v2 wizard reads/writes `jobs[0]`, so re-editing job N means
// moving it to the front; the remaining jobs keep their relative order.
// A snapshot of the pre-edit job list is stored so a step-0 "Go back"
// can restore the order exactly, and the picked index is stored so
// `finishEditJobDraftReducer` can splice the modified head back to its
// original position when the edit completes — without that splice,
// repeated Review-step edits on a multi-skill repeat order would
// accumulate a reversed skill order (each edit stranded the picked
// job at index 0, drifting the persisted order toward last-edited-
// first). Editing the already-active job (index <= 0) still snapshots
// so the cancel path is symmetric, but skips the reorder; the original
// index (0) is stored unchanged.
export const editJobDraftReducer = (
  prev: OrderStateV2,
  index: number,
): OrderStateV2 => {
  const snapshot = [...prev.jobs];
  if (index <= 0 || index >= prev.jobs.length) {
    return { ...prev, editSnapshot: snapshot, editIndex: 0 };
  }
  const rest = [...prev.jobs];
  const [picked] = rest.splice(index, 1);
  return {
    ...prev,
    jobs: [picked, ...rest],
    editSnapshot: snapshot,
    editIndex: index,
  };
};

// Restore the snapshot taken by editJobDraftReducer (step-0 "Go back"
// while an in-place edit is in progress). Returns the same state
// reference when there is no snapshot so unrelated navigation is a
// no-op. Wizard steps replace `jobs[0]` immutably, so the snapshot's
// original object references stay intact and restoring is exact.
export const cancelEditJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => {
  if (!prev.editSnapshot) return prev;
  return {
    ...prev,
    jobs: prev.editSnapshot,
    editSnapshot: null,
    editIndex: null,
  };
};

// Finalize an in-place edit on Continue-into-Review: take the modified
// `jobs[0]` and put it back at the index the user picked it from.
// `editJobDraftReducer` lifted the picked job to `jobs[0]` and left
// the rest of the array (other jobs, in their original relative order)
// at indices 1..N-1, so splicing the modified head back into that
// "rest" at the captured `editIndex` reconstructs the original
// ordering with only the edited job replaced. No-op when no edit is
// in flight or the jobs array is empty (defensive). Repeated edits
// without this rotation previously stranded each edited job at
// index 0, which is what caused the next repeat order's template to
// drift toward last-edited-first ordering.
export const finishEditJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => {
  if (!prev.editSnapshot || prev.editIndex === null) return prev;
  if (prev.jobs.length === 0) {
    return { ...prev, editSnapshot: null, editIndex: null };
  }
  const [head, ...rest] = prev.jobs;
  const targetIndex = Math.min(Math.max(prev.editIndex, 0), rest.length);
  const restored = [
    ...rest.slice(0, targetIndex),
    head,
    ...rest.slice(targetIndex),
  ];
  return {
    ...prev,
    jobs: restored,
    editSnapshot: null,
    editIndex: null,
  };
};

// Apply a fresh rate quote to `jobs[0]` — but only when the user hasn't
// already entered a pay/cost rate. Background refetches must never
// overwrite manual edits. Mirrors the legacy `useRateQuote` callback.
export const applyRateQuoteReducer = (
  prev: OrderStateV2,
  pay: number,
  cost: number,
): OrderStateV2 => {
  const job = prev.jobs[0];
  if (!job) return prev;
  if (job.pay_rate || job.cost_rate) return prev;
  const nextJobs = [...prev.jobs];
  nextJobs[0] = {
    ...job,
    pay_rate: pay,
    cost_rate: cost,
    original_pay_rate: pay,
    original_cost_rate: cost,
  };
  return { ...prev, jobs: nextJobs };
};
